import React, {ReactElement, useEffect, useMemo} from 'react';
import {Button} from '@customink/pigment-react';
import {useStoreSlug, useAppSelector, useCampaignStatus} from './index';
import {
  useGetStoreDetailsQuery,
  useLazyGetOrderSummaryQuery,
  useLazyGetBuyupQuery
} from '../api/storesApi';
import {selectPrimaryCampaign} from '../slices/storesSlice';
import {formatCurrency} from '../utils/helpers';
import services from '../api/remoteServices';
import {selectAdmin} from '../slices/userSlice';

export type GroupOrderOptionType = {
  title: string | ReactElement;
  message: string | ReactElement;
  button?: JSX.Element;
};

const ORDER_STATUS = {
  wcibmo: 'wcibmo',
  minimumNotMet: 'minimumNotMet',
  goalNotMet: 'goalNotMet'
} as const;

type OrderStatusKeys = keyof typeof ORDER_STATUS;

export const useGroupOrderOption = (): GroupOrderOptionType => {
  const storeSlug = useStoreSlug();
  useGetStoreDetailsQuery(storeSlug);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const campaignId = primaryCampaign?.id;
  const {isCampaignActive} = useCampaignStatus();

  const [triggerGetOrderSummary, {isFetching, data: campaignOrderData}] =
    useLazyGetOrderSummaryQuery();

  const [triggerGetBuyup, {data: buyupPath}] = useLazyGetBuyupQuery();

  useEffect(() => {
    const params = {
      urlSlug: storeSlug,
      campaignId
    };

    if (storeSlug && campaignId) {
      triggerGetOrderSummary(params);
    }
  }, [storeSlug, campaignId]);

  const admin = useAppSelector(selectAdmin);
  const isBuyUp = isCampaignActive && campaignOrderData?.buyupable;
  const isInkerCheckoutEnabled = campaignOrderData?.inkerCheckoutEnabled;
  const inkerCheckoutUrl = `${services.stores}/${storeSlug}/campaigns/${campaignId}/checkout`;

  useEffect(() => {
    const params = {
      urlSlug: storeSlug,
      campaignId
    };

    if (isBuyUp) {
      triggerGetBuyup(params);
    }
  }, [storeSlug, campaignId, isBuyUp]);

  const minQty = campaignOrderData?.minQty;
  const goalQty = campaignOrderData?.goalQty;
  const buyupAmount = campaignOrderData?.buyupAmount;
  const totalItemsQuantity = campaignOrderData?.totalItemsQuantity;
  const wcibmo = campaignOrderData?.wcibmo;
  const minDifference = minQty - totalItemsQuantity;
  const goalDifference = goalQty - totalItemsQuantity;

  const orderOptionMessages = {
    minimumNotMet: {
      title: 'Minimum Not Met',
      message: `The minimum quantity needed to continue is ${minQty}. To complete your Group Order, you or your group need to purchase ${minDifference} more.`,
      button: (
        <Button
          size="large"
          variant="secondary"
          type="submit"
          href={`${storeSlug}/manage/dashboard`}>
          Return To Dashboard
        </Button>
      )
    },
    goalNotMet: {
      title: 'One More Thing',
      message: (
        <>
          You haven&apos;t met your expected quantity, but that&apos;s okay. You
          have a couple of options:
          <ul>
            <li>
              Pay a balance of {formatCurrency(buyupAmount)} (the difference in
              the per-item price)
            </li>
            <li>Buy {goalDifference} more items to meet your goal</li>
          </ul>
        </>
      ),
      button: (
        <Button
          size="large"
          variant="secondary"
          {...(isInkerCheckoutEnabled && admin
            ? {href: inkerCheckoutUrl}
            : {type: 'submit', href: buyupPath})}>
          Pay Balance
        </Button>
      )
    },
    wcibmo: {
      title: 'Something is wrong with your order',
      message: wcibmo,
      button: (
        <Button
          size="large"
          variant="secondary"
          type="submit"
          href={`${storeSlug}/manage/dashboard`}>
          Return To Dashboard
        </Button>
      )
    }
  };

  const groupOrderStatus: OrderStatusKeys = useMemo(() => {
    if (campaignOrderData && !isFetching) {
      if (totalItemsQuantity < minQty) return ORDER_STATUS.minimumNotMet;
      if (wcibmo) return ORDER_STATUS.wcibmo;
      if (totalItemsQuantity < goalQty) return ORDER_STATUS.goalNotMet;
    }
    return null;
  }, [campaignOrderData, isFetching, orderOptionMessages, minQty, goalQty]);

  const groupOrderOptionMessage: GroupOrderOptionType = useMemo(() => {
    if (!groupOrderStatus) return null;

    return orderOptionMessages[groupOrderStatus];
  }, [groupOrderStatus, buyupPath]);

  return groupOrderOptionMessage;
};

export default useGroupOrderOption;
